/**
 * Recent ODM Submissions widget — shows the last few 837P batches sent to
 * Ohio Medicaid plus the response status (Transmitted → Acknowledged →
 * Accepted/Rejected → Paid). Auto-refreshes every 60s and exposes a
 * manual "Check now" button that triggers a poller run on the backend.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  ArrowsClockwise,
  CheckCircle,
  Clock,
  PaperPlaneTilt,
  XCircle,
  CurrencyDollar,
  CaretDown,
  CaretUp,
} from '@phosphor-icons/react';

const STATUS_STYLE = {
  Transmitted: {
    color: 'bg-slate-100 text-slate-700',
    icon: <Clock size={12} weight="fill" />,
    hint: 'Sitting in ODM inbox — awaiting pickup',
  },
  Acknowledged: {
    color: 'bg-blue-100 text-blue-800',
    icon: <CheckCircle size={12} weight="fill" />,
    hint: '999 received — file parsed correctly',
  },
  '999 Rejected': {
    color: 'bg-red-100 text-red-800',
    icon: <XCircle size={12} weight="fill" />,
    hint: '999 rejected — EDI parse failed; file needs corrections',
  },
  Accepted: {
    color: 'bg-emerald-100 text-emerald-800',
    icon: <CheckCircle size={12} weight="fill" />,
    hint: '277CA received — claims accepted by ODM adjudication',
  },
  Rejected: {
    color: 'bg-red-100 text-red-800',
    icon: <XCircle size={12} weight="fill" />,
    hint: '277CA returned a rejection — review claim errors',
  },
  Paid: {
    color: 'bg-purple-100 text-purple-800',
    icon: <CurrencyDollar size={12} weight="fill" />,
    hint: '835 remittance received — payment posted',
  },
};

const formatTimeAgo = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export const RecentOdmSubmissions = ({ refreshKey }) => {
  const { user } = useAuth();
  const canControlPoller = user?.entity_type === 'azai' && user?.azai_role === 'super_admin';
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    try {
      const res = await api.get('/odm-sftp/submissions', { params: { limit: 8 } });
      setSubmissions(res.data.submissions || []);
    } catch {
      // Silent — widget shouldn't block the page
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions, refreshKey]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const id = setInterval(fetchSubmissions, 60000);
    return () => clearInterval(id);
  }, [fetchSubmissions]);

  const handleCheckNow = async () => {
    setPolling(true);
    let pollIntervalId = null;
    let pollTimeoutId = null;
    try {
      const startRes = await api.post('/edi-responses/poll-now');
      const initial = startRes.data || {};
      if (initial.already_running) {
        toast.info('A poll is already running — watching its progress');
      } else {
        toast.info('Checking ODM outbound for new responses…');
      }

      // Poll status until done (or 5min cap)
      const startedAt = Date.now();
      const MAX_WAIT_MS = 5 * 60 * 1000;
      const tick = async () => {
        try {
          const statusRes = await api.get('/edi-responses/poll-status');
          const s = statusRes.data || {};
          if (!s || s.status === null || s.status === 'done' || s.status === 'error') {
            clearInterval(pollIntervalId);
            clearTimeout(pollTimeoutId);
            const counts = s.processed || {};
            const newCount =
              (counts['999'] || 0) +
              (counts['277'] || 0) +
              (counts['835'] || 0) +
              (counts.TA1 || 0);
            if (s.status === 'error') {
              toast.error(`Poll failed: ${(s.errors && s.errors[0]) || 'unknown error'}`);
            } else if (newCount > 0) {
              toast.success(
                `Found ${newCount} new response${newCount === 1 ? '' : 's'} from ODM`
              );
            } else {
              toast.info('No new responses yet — ODM is still processing');
            }
            fetchSubmissions();
            setPolling(false);
            return;
          }
          if (Date.now() - startedAt > MAX_WAIT_MS) {
            clearInterval(pollIntervalId);
            clearTimeout(pollTimeoutId);
            toast.warning('Poll is taking longer than expected — running in background');
            fetchSubmissions();
            setPolling(false);
          }
        } catch (e) {
          clearInterval(pollIntervalId);
          clearTimeout(pollTimeoutId);
          toast.error('Lost connection to poller status');
          setPolling(false);
        }
      };
      pollIntervalId = setInterval(tick, 3000);
      pollTimeoutId = setTimeout(() => {
        clearInterval(pollIntervalId);
        toast.warning('Poll is taking longer than expected — running in background');
        fetchSubmissions();
        setPolling(false);
      }, MAX_WAIT_MS);
    } catch (err) {
      const status = err.response?.status;
      toast.error(
        status === 403
          ? 'Only an AZAI super-admin with recent MFA can run the ODM response check'
          : err.response?.data?.detail || 'Failed to check responses'
      );
      setPolling(false);
    }
  };

  if (!loading && submissions.length === 0) return null;

  const pendingCount = submissions.filter((s) => s.response_status === 'Transmitted').length;
  const accumulatedClaims = submissions.reduce((acc, s) => acc + (s.claim_count || 0), 0);

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl overflow-hidden"
      data-testid="recent-odm-submissions"
    >
      {/* Header — div with role=button to avoid nesting <button> inside <button>
          (the right-side controls include real <button> elements). */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setCollapsed(!collapsed)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed(!collapsed);
          }
        }}
        className="w-full bg-gradient-to-r from-emerald-50 via-white to-white px-5 py-3 border-b border-slate-200 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer"
        data-testid="recent-odm-submissions-collapse-trigger"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
            <PaperPlaneTilt size={16} weight="fill" className="text-emerald-700" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-slate-900">
              Recent ODM Submissions
              {pendingCount > 0 && (
                <Badge className="ml-2 bg-amber-100 text-amber-800 text-[10px]">
                  {pendingCount} awaiting response
                </Badge>
              )}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {submissions.length} batch{submissions.length === 1 ? '' : 'es'} · {accumulatedClaims} claim
              {accumulatedClaims === 1 ? '' : 's'} sent · auto-refreshes every 60s
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Link
            to="/odm/submissions"
            className="text-xs text-indigo-700 hover:text-indigo-900 hover:underline font-medium"
            data-testid="view-odm-history-link"
          >
            View all
          </Link>
          {canControlPoller && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleCheckNow}
              disabled={polling}
              data-testid="check-odm-responses-now-btn"
            >
              <ArrowsClockwise
                size={14}
                className={`mr-1.5 ${polling ? 'animate-spin' : ''}`}
              />
              {polling ? 'Checking…' : 'Check now'}
            </Button>
          )}
          {collapsed ? <CaretDown size={14} className="text-slate-400" /> : <CaretUp size={14} className="text-slate-400" />}
        </div>
      </div>

      {/* Body */}
      {!collapsed && (
        <div className="divide-y divide-slate-100">
          {submissions.map((sub) => {
            const style = STATUS_STYLE[sub.response_status] || STATUS_STYLE.Transmitted;
            return (
              <Link
                key={sub.batch_id || sub.filename}
                to={`/odm/submissions/${encodeURIComponent(sub.batch_id || '')}`}
                className="block px-5 py-3 hover:bg-slate-50/80"
                data-testid={`odm-submission-${sub.batch_id}`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${style.color} text-[10px] inline-flex items-center gap-1`} title={style.hint}>
                        {style.icon}
                        {sub.response_status}
                      </Badge>
                      <span className="text-xs font-medium text-slate-900">
                        {sub.claim_count} claim{sub.claim_count === 1 ? '' : 's'}
                      </span>
                      {sub.skipped_count > 0 && (
                        <span className="text-xs text-amber-600">
                          ({sub.skipped_count} skipped)
                        </span>
                      )}
                      <span className="text-xs text-slate-400">·</span>
                      <span className="text-xs text-slate-500">
                        {formatTimeAgo(sub.submitted_at)}
                      </span>
                    </div>
                    <p
                      className="font-mono text-[11px] text-slate-500 mt-1 truncate"
                      title={sub.filename}
                    >
                      {sub.filename || sub.batch_id}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {sub.responses?.map((r) => (
                      <Badge
                        key={r.filename}
                        className={`text-[10px] ${
                          (r.status || '').toLowerCase().startsWith('reject')
                            ? 'bg-red-100 text-red-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                        title={`${r.filename} — ${r.status || ''}`}
                      >
                        {r.file_type} {r.status || ''}
                      </Badge>
                    ))}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
